import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { cn } from '../lib/utils';
import {
  firebaseEmailPasswordLogin,
  firebaseGoogleLogin,
  firebaseGoogleRedirectResult,
  firebaseGetCurrentUserToken,
  firebaseLogout,
} from '../lib/firebase';
import { CpHttpError, cpFetch, getDefaultTenantId } from '../lib/cpClient';

export function LoginModal() {
  const { t } = useTranslation();
  const { signIn, selectedTenant } = useAuthStore();
  const { showLoginModal, setShowLoginModal } = useUIStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [persist, setPersist] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const mapExchangeErrorToMessage = (err: unknown): string => {
    if (err instanceof CpHttpError) {
      if (err.status === 401) return 'Authentication failed. Please sign in again.';
      if (err.status === 403) return 'Your platform account is not registered. Contact your administrator.';
      if (err.status === 503) return 'Authentication service is temporarily unavailable. Please retry shortly.';
      if (err.code) return err.code;
      return `Login failed (HTTP ${err.status})`;
    }
    return err instanceof Error ? err.message : t('auth.error');
  };

  const shouldLogoutAfterExchangeError = (err: unknown): boolean => {
    return err instanceof CpHttpError ? err.status === 401 : true;
  };

  const exchangeFirebaseToken = async (firebaseIdToken: string) => {
    const tenantHint = (() => {
      try {
        const raw = localStorage.getItem('dc.auth');
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.tenant || parsed?.tenantId || selectedTenant || getDefaultTenantId() || 'dev';
      } catch {
        return selectedTenant || getDefaultTenantId() || 'dev';
      }
    })();

    const resp: any = await cpFetch('/auth/firebase/exchange', {
      method: 'POST',
      headers: { 'x-tenant-id': tenantHint },
      body: JSON.stringify({ id_token: firebaseIdToken }),
    });

    if (!resp?.success || !resp?.token) {
      throw new Error(resp?.error || 'Firebase exchange failed');
    }

    signIn(String(resp.token), persist);
    setShowLoginModal(false);
    setEmail('');
    setPassword('');
    setPersist(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      setLoading(true);
      if (!email.trim() || !password.trim()) {
        setError('Email and password are required');
        return;
      }

      const firebaseIdToken = await firebaseEmailPasswordLogin(email.trim(), password);
      await exchangeFirebaseToken(firebaseIdToken);
    } catch (err) {
      if (shouldLogoutAfterExchangeError(err)) {
        try {
          await firebaseLogout();
        } catch {
          // ignore
        }
      }
      setError(mapExchangeErrorToMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setInfo('');
    try {
      setLoading(true);
      const result = await firebaseGoogleLogin();
      if (result?.redirected) {
        setInfo('Popup blocked. Redirecting to Google…');
        return;
      }
      if (!result?.token) {
        throw new Error('Google sign-in failed');
      }
      await exchangeFirebaseToken(result.token);
    } catch (err) {
      if (shouldLogoutAfterExchangeError(err)) {
        try {
          await firebaseLogout();
        } catch {
          // ignore
        }
      }
      setError(mapExchangeErrorToMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowLoginModal(false);
    setEmail('');
    setPassword('');
    setError('');
    setInfo('');
    setPersist(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  useEffect(() => {
    let mounted = true;
    const checkRedirect = async () => {
      try {
        let token = await firebaseGoogleRedirectResult();
        if (!token) {
          token = await firebaseGetCurrentUserToken(2500);
        }
        let pending = false;
        try {
          pending = localStorage.getItem('dc.google_login_pending') === '1';
        } catch {
          pending = false;
        }
        if (!token || !mounted) {
          if (pending && mounted) {
            try {
              localStorage.removeItem('dc.google_login_pending');
            } catch {
              // ignore
            }
            setShowLoginModal(true);
            setError('Google sign-in did not complete. Please try again or use email/password.');
          }
          return;
        }
        setLoading(true);
        await exchangeFirebaseToken(token);
      } catch (err) {
        if (!mounted) return;
        if (shouldLogoutAfterExchangeError(err)) {
          try {
            await firebaseLogout();
          } catch {
            // ignore
          }
        }
        setError(mapExchangeErrorToMessage(err));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    checkRedirect();
    return () => {
      mounted = false;
    };
  }, [showLoginModal, t]);

  if (!showLoginModal) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-title"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 id="login-title" className="text-lg font-semibold">
            {t('auth.title')}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label={t('common.close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="btn-secondary w-full px-4 py-2"
            disabled={loading}
          >
            Sign in with Google
          </button>
          <div className="space-y-3">
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn('input', error && 'border-red-500 focus:ring-red-500')}
                placeholder="you@company.com"
                autoFocus
                disabled={loading}
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cn('input', error && 'border-red-500 focus:ring-red-500')}
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
          </div>

          {error && (
            <p className="mt-1 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          {info && !error && (
            <p className="mt-1 text-sm text-gray-600" role="status">
              {info}
            </p>
          )}

          <div className="flex items-center">
            <input
              id="persist-token"
              type="checkbox"
              checked={persist}
              onChange={(e) => setPersist(e.target.checked)}
              className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              disabled={loading}
            />
            <label htmlFor="persist-token" className="ml-2 block text-sm text-gray-700">
              {t('auth.persist')}
            </label>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary flex-1 px-4 py-2"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 px-4 py-2"
              disabled={loading || !email.trim() || !password.trim()}
            >
              {loading ? 'Signing in…' : t('auth.signIn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
